import React, { useState, useEffect, useMemo, useCallback, useRef, Suspense, lazy } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { toast } from 'react-toastify';
import moment from 'utils/moment';
import SectionHeader from '../../components/sectionHeader';
import icon from '../../icons/Tasks.png';
import Loading from '../../components/loading/loading';
import styles from './TasksList.module.scss';

import { fetchTasks, toggleCompleteTask, resetCompleteTask } from './redux/actions';
import Paginator from '../../components/paginator/paginator';

// Lazy load components
const Table = lazy(() => import(/* webpackChunkName: "Table" */ './components/Table'));
const CompleteTaskModal = lazy(() => import(/* webpackChunkName: "CompleteTaskModal" */ './components/CompleteTaskModal'));
const DateFilter = lazy(() => import(/* webpackChunkName: "DateFilter" */ '../../components/DateFilter/DateFilter'));

const MyTasks = ({ intl }) => {
  const { taskList, completeTask } = useSelector(state => state.tasks) || {};
  const totalCount = taskList?.count || 0;
  const dispatch = useDispatch();
  const location = useLocation();
  const isCompletedView = location.pathname.startsWith('/completed_tasks');

  const [loadingStatus, setLoadingStatus] = useState(true);
  const [completeModalStatus, setCompleteModalStatus] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState(null);

  const [selectionRange, setSelectionRange] = useState({
    date1: moment().startOf('day').toISOString(),
    date2: moment().endOf('day').toISOString()
  });

  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [isCompletedView]);

  const handlePageChange = useCallback((val) => {
    if ([page].includes(val)) return;
    setPage(val);
  }, [page]);

  const handleDateChange = useCallback((startDate, endDate, rangeName) => {
    const sDate = moment(startDate).toISOString();
    const eDate = moment(endDate).endOf('day').toISOString();

    setSelectionRange({ date1: sDate, date2: eDate });
    setDateRange(rangeName || null);
    setPage(1);
  }, []);

    // Initial fetch
    useEffect(() => {
      dispatch(resetCompleteTask());
      dispatch(fetchTasks(page, selectionRange.date1, selectionRange.date2, isCompletedView));
    }, [dispatch, page, selectionRange.date1, selectionRange.date2, isCompletedView]);

    useEffect(() => {
        setLoadingStatus(taskList?.loading);
        if (taskList?.error) {
            toast.error(taskList.error?.message || intl.formatMessage({ id: 'tasks.toast.fetch_failed', defaultMessage: 'Failed to fetch tasks' }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskList]);

  const filteredTasks = useMemo(() => {
    const tasks = taskList?.data || [];
    if (!Array.isArray(tasks) || tasks.length === 0) return [];
    return tasks;
  }, [taskList]);


  const sortedTasks = useMemo(() => [...filteredTasks].sort((a, b) => {
      const dateA = new Date(a.updated_on || a.event?.updated_on || 0).getTime();
      const dateB = new Date(b.updated_on || b.event?.updated_on || 0).getTime();
      // Latest record first (descending)
      return dateB - dateA;
    }), [filteredTasks]);

  const handleCompleteClick = useCallback((task) => {
    setTaskToComplete(task);
    setCompleteModalStatus(true);
  }, []);

  const handleConfirmComplete = useCallback(() => {
    if (taskToComplete && !completeTask?.loading) {
      dispatch(resetCompleteTask());
      setCompleteModalStatus(false);
      setLoadingStatus(true);
      dispatch(toggleCompleteTask(taskToComplete, '', moment().format()));
    }
  }, [dispatch, taskToComplete, completeTask]);

  const completeTaskPrev = useRef(completeTask);

  useEffect(() => {
    const prev = completeTaskPrev.current;
    const isNewSuccess = completeTask?.success && !prev?.success;
    const isNewError = completeTask?.error && completeTask.error !== prev?.error;

    if (isNewSuccess) {
          toast.success(intl.formatMessage({ id: 'tasks.toast.completed_success', defaultMessage: 'Task completed successfully' }));
      dispatch(fetchTasks(page, selectionRange.date1, selectionRange.date2, isCompletedView));
      setCompleteModalStatus(false);
      setTaskToComplete(null);
      dispatch(resetCompleteTask());
    }
    if (isNewError) {
      toast.error(
        completeTask.error?.message
          || intl.formatMessage({
            id: 'tasks.toast.complete_failed',
            defaultMessage: 'Failed to complete task'
          })
      );
      dispatch(resetCompleteTask());
    }

    completeTaskPrev.current = completeTask;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completeTask, dispatch, selectionRange.date1, selectionRange.date2, isCompletedView, page]);

  const handleCancelComplete = useCallback(() => {
    setCompleteModalStatus(false);
    setTaskToComplete(null);
  }, []);

  return (
    <>
      <SectionHeader
        icon={icon}
        translationID={isCompletedView ? 'tasks.completed.title' : 'tasks.title'}
        defaultTitle={isCompletedView ? 'Completed Tasks' : 'My Tasks'}
        subtitle={intl.formatMessage({ id: 'tasks.list.records', defaultMessage: '{count} records' }, { count: totalCount })}
      >
        <div style={{ paddingBottom: '8px', zIndex: 2 }}>
          <Suspense fallback={<Loading />}>
            {selectionRange.date1 && (
              <DateFilter
                onDateChange={handleDateChange}
                dates={[
                  moment(selectionRange.date1),
                  moment(selectionRange.date2)
                ]}
                range={dateRange}
              />
            )}
          </Suspense>
        </div>
      </SectionHeader>
      <div className={styles.tableContainer}>
        {!loadingStatus && sortedTasks.length === 0 && (
          <div className={styles.emptyState}>
            <FormattedMessage
              id="tasks.list.empty"
              defaultMessage="No tasks found for the selected date range."
            />
          </div>
        )}
        {(totalCount !== 0 || loadingStatus) && (
          <Suspense fallback={<Loading />}>
            <div className={styles.tableCard}>
              <Table
                info={sortedTasks}
                loadingStatus={loadingStatus}
                onCompleteClick={handleCompleteClick}
                hideCompletedCol={isCompletedView}
              />
              {totalCount > 10 && (
                <div className={styles.paginatorWrapper}>
                  <div className={styles.paginatorInfo}>
                    {(page - 1) * 10 + 1}-{Math.min(page * 10, totalCount)} of{' '}
                    <FormattedMessage id="tasks.list.results" defaultMessage="{count} Results" values={{ count: totalCount }} />
                  </div>
                  <Paginator
                    page={page}
                    totalRecords={totalCount}
                    pageLimit={10}
                    pageNeighbours={1}
                    onPageChanged={handlePageChange}
                  />
                </div>
              )}
            </div>
          </Suspense>
        )}
      </div>

      <Suspense fallback={null}>
        {completeModalStatus && (
          <CompleteTaskModal
            showIn={completeModalStatus}
            onClose={handleCancelComplete}
            onConfirm={handleConfirmComplete}
            taskName={taskToComplete?.title || taskToComplete?.event?.title}
            confirmDisabled={completeTask?.loading}
          />
        )}
      </Suspense>
    </>
  );
};

export default injectIntl(MyTasks);
